import React, { useState } from 'react';
import { CreateEventRequestSchema } from '@arenapulse/shared';
import { ApiClient } from '../lib/api-client';
import { getErrorMessage } from '../lib/error-messages';

interface CreateEventFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export function CreateEventForm({ onSuccess, onCancel }: CreateEventFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const result = CreateEventRequestSchema.safeParse({ name, description: description || undefined });

    if (!result.success) {
      // Get the first error message
      const firstError = result.error.errors[0]?.message || 'Please check your inputs.';
      setError(firstError);
      setIsSubmitting(false);
      return;
    }

    const response = await ApiClient.createEvent(result.data);

    if (response.success) {
      onSuccess();
    } else {
      setError(getErrorMessage(response.error.code));
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-border p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Create Event</h2>
        <button
          onClick={onCancel}
          className="text-muted hover:text-foreground"
          aria-label="Close"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-surface border border-destructive p-4">
            <div className="text-sm text-destructive">{error}</div>
          </div>
        )}

        <div>
          <label htmlFor="name" className="block text-sm font-medium leading-6 text-foreground">
            Event Name
          </label>
          <div className="mt-2">
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full rounded-md border-0 py-1.5 text-foreground shadow-sm ring-1 ring-inset ring-border placeholder:text-muted focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 px-3"
              placeholder="My Awesome Event"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium leading-6 text-foreground">
            Description (Optional)
          </label>
          <div className="mt-2">
            <textarea
              id="description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="block w-full rounded-md border-0 py-1.5 text-foreground shadow-sm ring-1 ring-inset ring-border placeholder:text-muted focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 px-3"
              placeholder="What is this event about?"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-foreground bg-surface border border-border rounded-md hover:bg-surface focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </form>
    </div>
  );
}
